"use client"

import { useEffect, useState } from "react"

export interface RelativeTimeLabels {
  justNow: string
  /** Used as a format source for e.g. "5 min ago". `{n}` placeholder. */
  minutesAgo: string
  /** Used as a format source for e.g. "2 h ago". `{n}` placeholder. */
  hoursAgo: string
}

/**
 * Formats a chat turn's ISO timestamp as a compact relative label.
 *
 *   <60s  → "Just now"
 *   <60m  → "5 min ago"
 *   <24h  → "2 h ago"
 *   else  → HH:MM in the user's locale
 */
export function formatRelativeTime(
  iso: string,
  labels: RelativeTimeLabels,
  now: number = Date.now()
): string {
  const ts = Date.parse(iso)
  if (Number.isNaN(ts)) return ""
  const deltaSec = Math.max(0, Math.round((now - ts) / 1000))
  if (deltaSec < 60) return labels.justNow
  const deltaMin = Math.round(deltaSec / 60)
  if (deltaMin < 60) return labels.minutesAgo.replace("{n}", String(deltaMin))
  const deltaHours = Math.round(deltaMin / 60)
  if (deltaHours < 24) return labels.hoursAgo.replace("{n}", String(deltaHours))
  return new Date(ts).toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
  })
}

/**
 * Re-renders every 30 s so "Just now" ticks over to "1 min ago"
 * without the user doing anything. Pauses after the chat is a day
 * old — the label stops changing at that point.
 */
export function useRelativeTime(
  iso: string,
  labels: RelativeTimeLabels
): string {
  const [, force] = useState(0)
  useEffect(() => {
    const ts = Date.parse(iso)
    if (Number.isNaN(ts)) return
    if (Date.now() - ts > 24 * 60 * 60 * 1000) return
    const id = window.setInterval(() => force((n) => n + 1), 30_000)
    return () => window.clearInterval(id)
  }, [iso])
  return formatRelativeTime(iso, labels)
}
